import type { TFunction } from 'i18next';
import { backendFields } from '@/api/errorDetails';

/** Exact tmm-api / Bean Validation phrases that have a catalog key. */
const EXACT_PHRASES: Record<string, string> = {
  required: 'errors.server.required',
  'must not be blank': 'errors.server.required',
  'must not be empty': 'errors.server.required',
  'must not be null': 'errors.server.required',
  invalid: 'errors.server.invalid',
  'must be a well-formed email address': 'errors.server.invalid',
  // TMMAPI-0160 U2: an opening date after today in the person's zone.
  'must be on or before today': 'errors.server.notFuture',
  // TMMAPI-0165: an account identifier the server refused.
  'already on another account': 'accounts.identifierTaken',
  'an IBAN, a CLABE, or an account number of 6-34 digits': 'accounts.identifierInvalid',
};

/** Translates a known server phrase or validation code; unknown prose never escapes. */
export function translateServerPhrase(message: string, t: TFunction): string {
  const key = Object.hasOwn(EXACT_PHRASES, message) ? EXACT_PHRASES[message] : undefined;
  if (key !== undefined) return t(key);
  if (/^must be at most \d+ characters$/.test(message)) return t('errors.server.tooLong');
  if (/^size must be between \d+ and \d+$/.test(message)) return t('errors.server.tooLong');
  if (message === 'REQUIRED') return t('errors.server.required');
  if (message === 'TOO_LONG') return t('errors.server.tooLong');
  if (message === 'INVALID') return t('errors.server.invalid');
  return t('errors.fieldInvalid');
}

/**
 * Places `backendFields(error)` onto the form's known API property names.
 * `leftover` is the server field names the form has no control for.
 *
 * `focusErrors` (the form's `useErrorFocus().focusErrors`, TMMAPP-0073) is called once the
 * fields are placed: after the errors render, focus goes to the form's summary or its first
 * invalid field in visual order, or — when no field matches — to the form-level alert (A11Y-010).
 */
export function applyBackendFields<K extends string>(
  error: unknown,
  known: readonly K[],
  setField: (field: K, message: string) => void,
  t: TFunction,
  focusErrors?: () => void,
): { placed: number; leftover: string[] } {
  const fields = backendFields(error);
  const knownSet = new Set<string>(known);
  let placed = 0;
  const leftover: string[] = [];
  for (const [field, raw] of Object.entries(fields)) {
    const message = translateServerPhrase(raw, t);
    if (knownSet.has(field)) {
      setField(field as K, message);
      placed += 1;
    } else {
      leftover.push(field);
    }
  }
  focusErrors?.();
  return { placed, leftover };
}

/**
 * Unknown controls get one form-level message, optionally with a known display label.
 */
export function validationAlert(
  result: { placed: number; leftover: string[] },
  _error: unknown,
  t: TFunction,
  fallback: string,
  labels: Readonly<Record<string, string>> = {},
): string {
  if (result.placed === 0 && result.leftover.length === 0) return fallback;
  if (result.leftover.length > 0) {
    const label = result.leftover
      .map((field) => (Object.hasOwn(labels, field) ? labels[field] : undefined))
      .filter(Boolean)
      .join(', ');
    return label ? t('errors.detailsAttentionLabel', { label }) : t('errors.detailsAttention');
  }
  return t('errors.checkFields');
}
